import { Metric, onCLS, onFCP, onINP, onLCP } from 'web-vitals';

import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';

let initialized = false;

/**
 * CLS는 값이 보통 0.0x~0.x 수준의 소수라 GA4 파라미터로 보내면 잘림/반올림 오차가 생기기 쉽다.
 * web.dev의 GA4 연동 가이드가 권장하는 대로 1000을 곱해 정수로 변환한다.
 * @see https://web.dev/articles/vitals-ga4
 */
const normalizeValue = (metric: Metric): number =>
  metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value);

/**
 * pushEvent는 내부적으로 dataLayer.push(GTM)와 이벤트 게이트웨이(이벤트로그) 양쪽으로 전송한다.
 * GA4는 필터를 여러 개 겹치면(국가×기기×지표 구간 등) 임계값 처리로 데이터를 숨기는 경우가 있어,
 * 이벤트로그 쪽에 사본을 남겨 GA4가 막힐 때 우회 조회할 수 있게 한다.
 */
const reportMetric = (metric: Metric): void => {
  pushEvent({
    eventType: EVENT_TYPE.DEBUG,
    eventName: EVENT_NAME.DEBUG__WEB_VITALS,
    eventParams: {
      metric_name: metric.name,
      metric_value: normalizeValue(metric),
      metric_rating: metric.rating,
      metric_id: metric.id,
    },
  });
};

/**
 * Core Web Vitals(LCP, INP, CLS, FCP)을 GTM(GA4)과 이벤트로그로 전송한다.
 * 앱 시작 시 1회만 호출한다. reportAllChanges를 켜지 않아 각 지표의 최종 확정값만 1회 전송된다
 * (LCP는 페이지가 hidden/unload될 때 확정되므로, 이탈 여부와 상관없이 값이 전송된다).
 * FCP는 Safari 14.1 미만처럼 Paint Timing API 미지원 브라우저에서는 콜백이 호출되지 않는다.
 */
export const initWebVitalsReporting = (): void => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  onLCP(reportMetric);
  onINP(reportMetric);
  onCLS(reportMetric);
  onFCP(reportMetric);
};
